import { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { Box, Text, TooltipRenderer } from '@machobear/design-system'
import { colors } from '@/styles'
import { Label } from '@/components/text'
import { Button } from '@/components/button'
import {
  ToasterItemIncompleteIcon,
  ToasterItemCompletedIcon,
  ToasterItemLockedIcon,
  ToasterItemErrorIcon
} from '@/components/icons'
import StatusBadge, { StatusBadgeType } from '@/components/status-badge'
import { useState } from '@/models/toaster/hooks'
import { StepProps, TaskData, State, StepButtonProps, BadgeState, TourItems } from './types'

type CallbackOrNode = StepProps['title']
type CallbackOrNodeDesc = StepProps['description']

const expandRenderPropsDesc = (
  value: CallbackOrNodeDesc,
  state: State,
  badge: BadgeState,
  data: TaskData
) => {
  if (typeof value !== 'function') return value
  return value(state, badge, data)
}

const expandRenderProps = (value: CallbackOrNode, state: State, badge: BadgeState) => {
  if (typeof value !== 'function') return value
  return value(state, badge)
}

const TaskStep = styled.div`
  display: flex;
  flex-direction: column;
`

const StepHeading = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0 8px;
`

const StepTitle = styled(Label)<{ state: State | 'none' }>`
  display: flex;
  align-items: center;
  text-align: left;
  ${({ state }) =>
    state !== 'none'
      ? `
    color: ${colors.grey56};
`
      : `
    font-weight: 700;
  `}

  ${({ state }) =>
    state === 'complete' &&
    `
    text-decoration-line: line-through;
    text-decoration-style: solid;
    color: ${colors.grey56};
  `}

  ${({ state }) =>
    state === 'error' &&
    `
    color: ${colors.red40};
  `}
`

const StepDescription = styled.div`
  padding-top: 8px;
  padding-left: 28px;
  font-family: 'Inter';
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  color: ${colors.grey32};
`

const getStepIcon = (state: State) => {
  switch (state) {
    case 'incomplete':
      return ToasterItemIncompleteIcon
    case 'complete':
      return ToasterItemCompletedIcon
    case 'locked':
      return ToasterItemLockedIcon
    case 'error':
    default:
      return ToasterItemErrorIcon
  }
}

const isButton = (state: State, { onClick, to }: StepButtonProps) => {
  const incomplete = state === 'incomplete'
  const hasAction = onClick || to
  return incomplete && hasAction
}

const getBadgeType = (state: BadgeState): StatusBadgeType => {
  switch (state) {
    case 'draft':
      return 'warning'
    case 'pending':
    case 'none':
    default:
      return 'default'
  }
}

type CommonProps = Pick<StepProps, 'to' | 'onClick'> & { state: State }
type ClickableProps = CommonProps & {
  children?: React.ReactNode
  data: TaskData
  hasTooltip?: boolean
  tourItems: TourItems
}

type HeadingProps = CommonProps & Pick<StepProps, 'title' | 'only'> & { badge: BadgeState }

const Clickable: React.FC<ClickableProps> = ({
  state,
  onClick,
  to,
  children,
  data,
  tourItems,
  hasTooltip = false
}) => {
  if (!isButton(state, { onClick, to })) return <>{children}</>
  const handleClick = () => {
    if (typeof onClick === 'function') onClick(data, tourItems)
  }
  // Prevent double nested button from tooltip
  const rest = hasTooltip ? { as: 'div' } : {}
  return (
    <Button theme="text" size="small" space="fit" onClick={handleClick} to={to} {...rest}>
      {children}
    </Button>
  )
}

const Heading: React.FC<HeadingProps> = ({ state, onClick, to, title, badge = 'none', only }) => {
  const { t } = useTranslation()
  const Icon = useMemo(() => getStepIcon(state), [state])
  const getBadgeText = useCallback(
    (state: BadgeState) => {
      switch (state) {
        case 'draft':
          return t('b.toaster.view.badge.draft.label', 'Draft')
        case 'pending':
          return t('b.toaster.view.badge.pending.label', 'Pending')
        case 'none':
        default: {
          return ''
        }
      }
    },
    [t]
  )
  return (
    <StepHeading>
      {(!only || (only && state === 'locked')) && <Icon width={20} height={20} />}
      <StepTitle
        fontSize="12px"
        type="small"
        state={!isButton(state, { onClick, to }) ? state : 'none'}
      >
        {title}
      </StepTitle>
      {badge !== 'none' && (
        <StatusBadge type={getBadgeType(badge)} title={getBadgeText(badge)} size="small" />
      )}
    </StepHeading>
  )
}

const empty = {}
const ToasterStep: React.FC<StepProps> = ({
  id,
  title: initialTitle,
  description,
  onClick,
  to,
  tooltip,
  only
}) => {
  const { badge, state, data: initialData, tourItems } = useState(id)
  const data = initialData ?? empty
  const newState = !only ? state : 'incomplete'
  const desc = useMemo(
    () => expandRenderPropsDesc(description, state, badge, data),
    [description, state, badge, data]
  )
  const title = useMemo(
    () => expandRenderProps(initialTitle, state, badge),
    [initialTitle, state, badge]
  )
  const stepTooltip = tooltip && tooltip(state)

  return (
    <TaskStep>
      {stepTooltip ? (
        <TooltipRenderer id="toaster" tooltip={<Text size="small">{stepTooltip.content}</Text>}>
          {({ triggerProps }) => (
            <Box {...triggerProps}>
              <Clickable
                onClick={onClick}
                to={to}
                state={newState}
                data={data}
                hasTooltip
                tourItems={tourItems}
              >
                <Heading
                  onClick={onClick}
                  to={to}
                  state={newState}
                  title={title}
                  badge={badge}
                  only={only}
                />
              </Clickable>
            </Box>
          )}
        </TooltipRenderer>
      ) : (
        <Clickable onClick={onClick} to={to} state={newState} data={data} tourItems={tourItems}>
          <Heading
            onClick={onClick}
            to={to}
            state={newState}
            title={title}
            badge={badge}
            only={only}
          />
        </Clickable>
      )}
      {desc && <StepDescription>{desc}</StepDescription>}
    </TaskStep>
  )
}

export default ToasterStep
